// @ts-check

import * as Sentry from "@sentry/react";
import { QueryClient, useMutation } from "@tanstack/react-query";
import z from "zod";
import { ACTIVE_CHAT_SESSION_ID_QUERY_KEY, ChatSessionQueryKeys, useCreateChatSession } from "../sessions";
import { ChatMessageQueryKeys, ChatMessageQueryActions } from "../messages/query";
import { ChatReportQueryKeys } from "../reports";
import { ChatImagesQueryKeys } from "../images";
import { useHistory } from "react-router-dom";
import { toast } from "sonner";
import { AgentChatChunkDataContentSchema } from "../schema";
import { activeStreamsAtom, ActiveStreamsAtomActions } from ".";
import { store } from "@/lib/store";
import { parseAgentTextResponse } from "@/utils/agent-response";
import { getParsedToolParam } from "@/components/chat-bubble/tools";
import { applyReviewProgressEvent } from "../canvas/reviewProgress";
import { openCreatedDocument } from "../canvas/panelEvents";
import { GenerateReportArgsSchema, GenerateReportResultSchema } from "@/components/chat-bubble/tools/generate-report";
import { createdDocument, extendMutationOptions, OPEN_REPORT_FORM_EVENT, OPEN_REPORT_PANEL_EVENT } from "../utils";
import { getSessionStream, SSEStreamDisconnectedError, StreamEvents, StreamEndedEventDataSchema, ChatProviderError, ErrorCodes  } from "../api";
import ApiService from "@minohealth/common/services/ApiService";
import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";
import { setUser } from "@minohealth/common/services/DataStoreService/reducers/AppReducer";

/**
  * Polyfill for Safari - converts ReadableStream to async iterable
  * @template T
  * @param {ReadableStream<T>} stream
  */
async function* readableToAsyncIterable(stream) {
  const reader = stream.getReader();
  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) return;
      yield value;
    }
  } finally {
    reader.releaseLock();
  }
}

/**
 * @param {ReadableStream<import("../api").StreamEventsPayload>} readable
 * @param {Object} params
 * @param {number} [params.resumeIndex]
 * @param {string} params.sessionId
 * @param {() => string} params.getUserToken
 * @param {(payload: import("../api").StreamEventsPayload) => void} [params.onStreamEvent]
 */
async function handleStream(readable, { resumeIndex = 0, sessionId, getUserToken, onStreamEvent }) {
  let currentChunkIndex = -1
  const stream = readable.pipeThrough(
    /** @type {TransformStream<import("../api").StreamEventsPayload, import("../api").StreamEventsPayload>} */(
      new TransformStream({
        transform(chunk, controller) {
          currentChunkIndex++
          if(currentChunkIndex < resumeIndex) return
          controller.enqueue(chunk)
        }
      })
    )
  )

  try {
    for await (const chunk of readableToAsyncIterable(stream)) {
      switch (chunk.event) {
        case StreamEvents.STREAM_STARTED:
          onStreamEvent?.(chunk)
          break;
        case StreamEvents.STREAM_CONTENT:
          onStreamEvent?.(chunk)
          break;
        case StreamEvents.STREAM_STRUCTURED_CONTENT:
          onStreamEvent?.(chunk)
          break;
        case StreamEvents.STREAM_ENDED:
          onStreamEvent?.(chunk)

          return chunk.data
          break;
        default:
          console.warn(`Unknown event type: ${chunk.event}`)
          break;
      }
    }
  } catch(error) {
    if (error instanceof SSEStreamDisconnectedError) {
      return resumeStream(sessionId, {
        resumeIndex: currentChunkIndex + 1,
        getUserToken,
        onStreamEvent,
      })
    }

    throw error
  }
}

/**
 * @param {string} sessionId
 * @param {Object} params
 * @param {number} params.resumeIndex
 * @param {() => string} params.getUserToken // * Uses a getter to ensure the token is up-to-date
 * @param {(payload: import("../api").StreamEventsPayload) => void} [params.onStreamEvent]
 * @param {AbortSignal} [params.signal]
 * @returns {Promise<z.infer<typeof import("../api").StreamEndedEventDataSchema>>}
 */
async function resumeStream(sessionId, { resumeIndex, getUserToken, onStreamEvent, signal }) {
  const readable = getSessionStream(sessionId, getUserToken(), { signal })
  return handleStream(readable, {
    resumeIndex,
    sessionId,
    getUserToken,
    onStreamEvent,
  })
}

/**
 * @param {QueryClient} client
 * @param {z.infer<typeof import("../api").StreamEndedEventDataSchema>} data 
 * @param {string} sessionId
 */
async function setStreamQueriesData(client, data, sessionId) {
  await client.cancelQueries({ queryKey: ChatSessionQueryKeys.CHAT_SESSION(sessionId) })
  await client.cancelQueries({ queryKey: ChatMessageQueryKeys.CHAT_MESSAGES(sessionId) })
  await client.cancelQueries({ queryKey: ChatReportQueryKeys.CHAT_SESSION_REPORTS(sessionId) })
  await client.cancelQueries({ queryKey: ChatImagesQueryKeys.CHAT_SESSION_IMAGES(sessionId) })

  // Append committed messages to the messages cache (not the session cache).
  if (data.messages.length > 0) {
    client.setQueryData(
      ChatMessageQueryKeys.CHAT_MESSAGES(sessionId),
      ChatMessageQueryActions.APPEND_MESSAGES(data.messages)
    )
  }

  client.setQueryData(
    ChatSessionQueryKeys.CHAT_SESSION(sessionId),
    /** @param {ChatSession} session */
    session => ({
      ...session,
      images: [...session.images, ...data.images.map(image => image._id)],
      files: [...session.files, ...data.files],
      reports: [...session.reports, ...data.reports.map(report => report._id)],
    })
  )

  client.setQueryData(
    ChatReportQueryKeys.CHAT_SESSION_REPORTS(sessionId),
    /** @param {ChatReport[]} reports */
    reports => [...(reports ?? []), ...data.reports]
  )

  client.setQueryData(
    ChatImagesQueryKeys.CHAT_SESSION_IMAGES(sessionId),
    /** @param {ChatImage[]} images */
    images => [...(images ?? []), ...data.images]
  )

  // The server adds a document that the model creates, and the stream does not report it.
  if (createdDocument(data.messages)) {
    client.invalidateQueries({ queryKey: ChatSessionQueryKeys.CHAT_SESSION(sessionId) })
  }
}

/** @param {Error} error */
function isInsufficientBalanceError(error) {
  return error?.message != null && (
    error.message.startsWith("Insufficient tokens") ||
    error.message.startsWith("Insufficient credits")
  )
}

/** @param {Error} error */
function isChatLimitError(error) {
  return error?.message === "Chat length exceeds limit" 
}

/**
 * @param {Error} error
 * @param {() => void} gotoTopup
 */
function showInsufficientBalanceToast(error, gotoTopup) {
  toast.error("Insufficient balance", {
    duration: 1000 * 10, // 10 seconds
    description: "Please add more credits to continue",
    action: {
      label: "Top Up Now",
      onClick: () => {
        gotoTopup()
      },
    }
  })
}

/**
 * @param {Error} error
 * @param {() => void} startNewChatSession
 */
function showChatLimitToast(error, startNewChatSession) {
  toast.error("Chat length exceeds limit", {
    duration: 1000 * 10, // 10 seconds
    description: "Please start a new chat to continue the conversation",
    action: {
      label: "New Chat",
      onClick: () => {
        startNewChatSession()
      }
    }
  })
}

/**
 * @param {z.infer<typeof StreamEndedEventDataSchema>} data
 */
function previewLastGeneratedReport(data) {
  if (data.reports == null) return
  if (data.reports.length === 0) return

  const lastReportEntry = data.reports[data.reports.length - 1]
  document.dispatchEvent(new CustomEvent(OPEN_REPORT_PANEL_EVENT, {
    detail: {
      report: {
        type: "ref",
        ref: lastReportEntry.reportId
      }
    }
  }))
}

function useStreamSuccessHandler() {
  const { user } = useSelector(state => state.app.datastate)
  const dispatch = useDispatch()

  async function updateUserDetails() {
    try {
      const { status, data } = await ApiService(user.userToken).GetUser({ id: user._id });
      if (status.toString().match(/^2/)) {
        dispatch(setUser({ ...data.payload, userToken: data?.refreshedToken }));
      }
    } catch (error) {
      Sentry.captureException(error, {
        extra: { 
          context: 'updateUserDetails - user profile retrieval',
          userId: user._id,
          hasUserToken: !!user.userToken
        }
      });
      // showErrorMessage( "Failed to retrieve user profile", "System Error");
    }
  };

 /** @param {z.infer<typeof import("../api").StreamEndedEventDataSchema>} data */
  function handleStreamSuccess(data) {
    updateUserDetails()
    previewLastGeneratedReport(data)
  }

  return handleStreamSuccess
}

/**
 * @param {Object} [options]
 * @param {boolean | ((error: Error) => boolean)} [options.suppressErrors]
 * @param {(variables: { sessionId: string }) => void } [options.getSessionStream]
 * @param {string} [options.sessionId]
 */
function useStreamErrorHandler({ suppressErrors = false, getSessionStream, sessionId } = {}) {
  const history = useHistory()
  const { mutate: createNewChatSession } = useCreateChatSession()

  /** @param {Error} error */
  function handleStreamError(error) {
    // TODO: Have better styling for toasts

    // TODO: Invalidate chat query if error is NOT_FOUND(Stream finished on the server)

    const shouldSuppress = (() => {
      if (typeof suppressErrors === "function") return suppressErrors(error)
      return suppressErrors
    })()

    if(!shouldSuppress) {
      if(isInsufficientBalanceError(error)) {
        showInsufficientBalanceToast(error, () => history.push("/payment/topup"))
      } else if(isChatLimitError(error)) {
        showChatLimitToast(error, () => createNewChatSession())
      } else if(error instanceof ChatProviderError && error.code === ErrorCodes.ACTIVE_STREAM_EXISTS) {
        toast.error("A response is already being generated. Connecting you to it...")
      } else {
        toast.error(error.message)
      }
    }

    if (error instanceof ChatProviderError && error.code === ErrorCodes.ACTIVE_STREAM_EXISTS && getSessionStream) {
      getSessionStream({ sessionId })
    }
  }

  return handleStreamError
}

/**
 * @param {string} sessionId
 * @param {ChatMessagePart} content
 */
function addChatStreamContent(sessionId, content) {
  store.set(activeStreamsAtom,
    ActiveStreamsAtomActions.APPEND_RESPONSE_CHUNK(
      sessionId,
      "structured-content",
      content
    )
  )
}

/** @param {string} chatSessionId */
function openReportForm(chatSessionId) {
  document.dispatchEvent(new CustomEvent(OPEN_REPORT_FORM_EVENT, {
    detail: {
      chatSessionId
    }
  }))
}

/**
 * @param {string} sessionId
 * @param {z.infer<typeof GenerateReportArgsSchema> & z.infer<typeof GenerateReportResultSchema>} reportResult
 */
function previewReportToolResult(sessionId, reportResult) {
  const activeChatSessionId = new URLSearchParams(window.location.search).get(ACTIVE_CHAT_SESSION_ID_QUERY_KEY)
  if (sessionId != activeChatSessionId) return

  document.dispatchEvent(new CustomEvent(OPEN_REPORT_PANEL_EVENT, {
    detail: {
      report: {
        type: "report",
        report: {
          patientInfo: {
            name: "",
            age: reportResult.age,
            gender: reportResult.gender,
            id: ""
          },
          modality: reportResult.modality,
          image: "",
          original: reportResult.content,
          current: reportResult.content,
        }
      }
    }
  }))
}

/**
 * @param {string} sessionId
 */
function openReportGenerationFormIfTagPresent(sessionId) {
  const streamRecord = store.get(activeStreamsAtom)[sessionId]
  if (streamRecord == null) return
  if (streamRecord.type !== "chat") return
  if (streamRecord.response?.type !== "structured-content") return

  const concatenatedContent = streamRecord.response.content
    .filter(part => (
      part.event === "RunContent" || part.event === "RunResponseContent"
    ))
    .map(part => part.data.content)
    .join("")

  const parsedContent = parseAgentTextResponse(concatenatedContent);
  const hasReportGenerationTag = parsedContent.some(part => part.type === 'report_generation_tag');
  if (!hasReportGenerationTag) return

  openReportForm(sessionId)
}

/**
 * @param {string} sessionId
 */
function previewGeneratedReportIfToolCalled(sessionId) {
  const streamRecord = store.get(activeStreamsAtom)[sessionId]
  if (streamRecord == null) return
  if (streamRecord.type !== "chat") return
  if (streamRecord.response?.type !== "structured-content") return

  const lastPartIndex = streamRecord.response.content.length - 1
  const lastPart = streamRecord.response.content[lastPartIndex]
  if (lastPart.event !== "ToolCallCompleted") return
  if(lastPart.data.tool_name !== "generate_report") return

  let matchingStartPart = null
  for(let i = lastPartIndex - 1; i >= 0; i--) {
    const part = streamRecord.response.content[i]
    if(part.event === "ToolCallCompleted" && part.data.tool_name === "generate_report") break
    if(part.event === "ToolCallStarted" && part.data.tool_name === "generate_report") {
      matchingStartPart = part
      break
    }
  }
  if(matchingStartPart == null) return

  const reportArgs = matchingStartPart.data.args
  const reportResult = lastPart.data.tool_result
  
  try {
    const parsedReportArgs = getParsedToolParam(reportArgs, GenerateReportArgsSchema);
    const parsedReportResult = getParsedToolParam(reportResult, GenerateReportResultSchema);

    if(parsedReportResult == null || parsedReportResult instanceof Error) return
    if(parsedReportArgs == null || parsedReportArgs instanceof Error) return

    previewReportToolResult(sessionId, { ...parsedReportArgs, ...parsedReportResult });
  } catch (error) {
    console.error('Failed to parse report generation result:', error);
  }
}

/**
 * @param {z.infer<typeof AgentChatChunkDataContentSchema>} event
 * @param {string} sessionId
 */
function handleAgentStreamEvent(event, sessionId) {
  switch (event.event) {
    case 'session_info':
      break;

    case 'RunStarted':
      break;

    case 'ReviewProgress':
    case 'EditDrafted':
      applyReviewProgressEvent(sessionId, event)
      break;

    case 'RunCompleted':
      // Saved messages keep this event, and the reasoning section closes on it.
      addChatStreamContent(
        sessionId,
        { event: event.event, data: event }
      )
      openReportGenerationFormIfTagPresent(sessionId)
      break;

    case 'RunResponseContent':
      addChatStreamContent(
        sessionId,
        { event: event.event, data: event }
      )
      break;

    case 'RunContent':
      addChatStreamContent(
        sessionId,
        { event: event.event, data: event }
      )
      break;

    case 'ToolCallStarted':
      addChatStreamContent(
        sessionId,
        { event: event.event, data: event }
      )
      break;

    case 'ToolCallCompleted':
      addChatStreamContent(
        sessionId,
        { event: event.event, data: event }
      )

      previewGeneratedReportIfToolCalled(sessionId)
      // A document the agent has just created opens beside the chat.
      openCreatedDocument(sessionId, event)
      break;

    case 'ReasoningContent':
      addChatStreamContent(
        sessionId,
        { event: event.event, data: event }
      )
      break;

    case 'ReasoningStarted':
      break;

    case 'ReasoningStep':
      addChatStreamContent(
        sessionId,
        { event: event.event, data: event }
      )
      break;

    case 'ReasoningCompleted':
      break;

    case 'DelegationStarted':
      addChatStreamContent(
        sessionId,
        { event: event.event, data: event }
      )
      break;

    case 'DelegationCompleted':
      addChatStreamContent(
        sessionId,
        { event: event.event, data: event }
      )
      break;

    case 'SubAgentStarted':
      addChatStreamContent(
        sessionId,
        { event: event.event, data: event }
      )
      break;

    case 'SubAgentCompleted':
      addChatStreamContent(
        sessionId,
        { event: event.event, data: event }
      )
      break;

    case 'SubAgentRunStarted':
      addChatStreamContent(
        sessionId,
        { event: event.event, data: event }
      )
      break;

    case 'SubAgentRunCompleted':
      addChatStreamContent(
        sessionId,
        { event: event.event, data: event }
      )
      break;

    case 'PlanCreated':
      break;

    case 'PlanUpdated':
      break;

    case 'MemoryCleanedUp':
      break;

    case 'stream_complete':
      break;

    case 'research_complete':
      break;

    case 'error':
      break;

    default:
      console.warn('Unknown agent event type:', JSON.stringify(event, null, 2));
  }
}

/**
 * @template {z.infer<typeof import("../api").StreamEndedEventDataSchema>} TData
 * @template {Error} TError
 * @template {unknown} TVariables
 * @template {unknown} TOnMutateResult
 * 
 * @param {Parameters<typeof extendMutationOptions<TData, TError, TVariables, TOnMutateResult>>[0]} mutationOptions 
 * @param {Parameters<typeof extendMutationOptions<TData, TError, TVariables, TOnMutateResult>>[1]} extraOptions
 * @param {Parameters<typeof useStreamErrorHandler>[0]} options
 */
function useStreamMutation(mutationOptions, extraOptions, options) {
  const handleStreamSuccess = useStreamSuccessHandler()
  const handleStreamError = useStreamErrorHandler(options)

  return useMutation(
    extendMutationOptions(mutationOptions, {
      ...extraOptions,
      onSuccess (data, variables, mutateResult, context) {
        handleStreamSuccess(data)
        extraOptions?.onSuccess?.(data, variables, mutateResult, context)
      },
      onError (error, variables, mutateResult, context) {
        if(error.name !== "AbortError") {
          handleStreamError(error)
        }

        extraOptions?.onError?.(error, variables, mutateResult, context)
      },
    })
  )
}

export {
  handleStream,
  setStreamQueriesData,
  handleAgentStreamEvent,
  useStreamMutation,
}
